import dayjs from "dayjs";
import { cloneDeep, get, isEmpty, unset } from "lodash";
import { API_DATE_FORMAT, DATE_FORMAT } from "utils/constants";

const EMPTY_FIELD_REQUIRED_MESSAGES = [
  "ce champ est obligatoire",
  "ce champ est obligatoire. veuillez le compléter",
  "ce champ est obligatoire. merci de sélectionner une option",
  "obligatoire",
  "required",
  "this field is required",
];

const pendingClears = new WeakMap();

const isEmptyFieldRequiredMessage = (error) => {
  const text = (Array.isArray(error) ? error.join(" ") : String(error))
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
  return EMPTY_FIELD_REQUIRED_MESSAGES.includes(text);
};

export const hasFilledFieldValue = (value) => {
  if (value === null || value === undefined || value === false) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (dayjs.isDayjs(value)) return value.isValid();
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const pruneEmptyBranches = (node) => {
  if (!node || typeof node !== "object" || Array.isArray(node)) return;
  Object.keys(node).forEach((key) => {
    pruneEmptyBranches(node[key]);
    const child = node[key];
    if (
      child &&
      typeof child === "object" &&
      !Array.isArray(child) &&
      Object.keys(child).length === 0
    ) {
      delete node[key];
    }
  });
};

const withoutNamedError = (errors, name) => {
  if (!name || !get(errors, name)) return errors;
  const next = cloneDeep(errors || {});
  unset(next, name);
  pruneEmptyBranches(next);
  return next;
};

// Formik keeps the previous required error until the whole schema resolves.
// A zip-code check in that schema can take several seconds, so a filled field
// stays blocking. Drop that field's error as soon as it has a value.
// setFormikState reads the latest reducer state, so two fields cleared in the
// same tick cannot put each other's error back.
export const clearNamedError = ({
  errors,
  setErrors,
  setFormikState,
  name,
}) => {
  if (!name) return;
  if (typeof setFormikState === "function") {
    setFormikState((state) => {
      const current = get(state, "errors") || {};
      const next = withoutNamedError(current, name);
      if (next === current) return state;
      return { ...state, errors: next };
    });
    return;
  }
  if (typeof setErrors !== "function") return;
  const pending = pendingClears.get(setErrors);
  const source = pending && pending.base === errors ? pending.next : errors;
  const next = withoutNamedError(source, name);
  if (next === source) return;
  pendingClears.set(setErrors, { base: errors, next });
  setErrors(next);
};

export const shownFieldError = (value, error) => {
  if (!error || !hasFilledFieldValue(value)) return error || "";
  if (isEmptyFieldRequiredMessage(error)) return "";
  return error;
};

export const noSpace = (e) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

export const bindInputProps = ({
  name,
  values,
  handleChange,
  setFieldTouched,
  setErrors,
  setFormikState,
  touched,
  errors,
  onBlur = () => {},
}) => {
  return {
    value: get(values, name, undefined),
    onChange: (eventOrValue) => {
      handleChange(name)(eventOrValue);
      const value =
        eventOrValue && eventOrValue.target
          ? eventOrValue.target.type === "checkbox"
            ? eventOrValue.target.checked
            : eventOrValue.target.value
          : eventOrValue;
      if (hasFilledFieldValue(value)) {
        clearNamedError({ errors, setErrors, setFormikState, name });
      }
    },
    onBlur: (e) => {
      const value = get(e, "target.value");
      setFieldTouched(name);
      onBlur(value);
    },
    touched: get(touched, name, false),
    errors: shownFieldError(
      get(values, name, undefined),
      get(errors, name, ""),
    ),
    name,
  };
};

/** Formik + Type B text fields — shared Custom input (conseiller) */
export const bindTextFieldInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  setErrors,
  setFormikState,
  touched,
  errors,
  onBlur = () => {},
}) => ({
  type: "custom",
  normalizeText: true,
  value: get(values, name, "") ?? "",
  errors: shownFieldError(get(values, name, ""), get(errors, name, "")),
  touched: get(touched, name, false),
  name,
  onChange: ({ target: { value } }) => {
    setFieldValue(name, value);
    if (hasFilledFieldValue(value)) {
      clearNamedError({ errors, setErrors, setFormikState, name });
    }
  },
  onBlur: (e) => {
    setFieldTouched(name, true);
    onBlur(get(e, "target.value"));
  },
});

/** Formik + Type B text fields — Web B2C Simple input (Comparison/*) */
export const bindWebTextFieldInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  setErrors,
  setFormikState,
  handleChange,
  touched,
  errors,
  onBlur = () => {},
}) => ({
  normalizeText: true,
  value: get(values, name, "") ?? "",
  errors: shownFieldError(get(values, name, ""), get(errors, name, "")),
  touched: get(touched, name, false),
  name,
  onChange: ({ target: { value } }) => {
    if (setFieldValue) {
      setFieldValue(name, value);
    } else {
      handleChange?.({ target: { name, value } });
    }
    if (hasFilledFieldValue(value)) {
      clearNamedError({ errors, setErrors, setFormikState, name });
    }
  },
  onBlur: (e) => {
    setFieldTouched?.(name, true);
    onBlur(get(e, "target.value"));
  },
});

export const bindDateInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  setErrors,
  setFormikState,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const value = get(values, name, undefined);
  // Keep the same dayjs instance when Formik already stores one. Recreating
  // `dayjs(value)` on every render changes object identity and makes
  // Ant Design's rc-picker reset the open calendar back to the selected month.
  const dateValue = value
    ? dayjs.isDayjs(value)
      ? value
      : dayjs(value)
    : null;

  return {
    // Controlled only — avoid defaultValue, which fights Ant Design's panel
    // while the user navigates months/years with an existing selection.
    value: dateValue,
    onChange: (date) => {
      const nextDate = date
        ? date.set("hour", 4).set("minute", 0).set("second", 0)
        : null;
      setFieldValue(name, nextDate);
      if (hasFilledFieldValue(nextDate)) {
        clearNamedError({ errors, setErrors, setFormikState, name });
      }
    },
    onBlur: (e) => {
      if (setFieldTouched) setFieldTouched(name);
      const rawValue = get(e, "target.value");
      // Commit typed dates only when the day actually changes. Focusing the
      // calendar panel blurs the input; rewriting the same day would reset
      // Ant Design's open month/year navigation.
      const parsedDate = dayjs(rawValue, DATE_FORMAT, true);
      if (parsedDate.isValid()) {
        const nextDate = parsedDate
          .set("hour", 4)
          .set("minute", 0)
          .set("second", 0);
        if (!dateValue || !dateValue.isSame(nextDate, "day")) {
          setFieldValue(name, nextDate);
          clearNamedError({ errors, setErrors, setFormikState, name });
        }
      }
      onBlur(rawValue);
    },
    touched: get(touched, name, false),
    errors: shownFieldError(dateValue, get(errors, name, "")),
    name,
  };
};

export const bindDateRangeInputProps = ({
  name,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const [startValue, endValue] = get(values, name, undefined);
  const value = [startValue && dayjs(startValue), endValue && dayjs(endValue)];
  return {
    value,
    defaultValue: value,
    onChange: ([startDate, endDate]) => {
      setFieldValue(name, [
        startDate.format(API_DATE_FORMAT),
        endDate.format(API_DATE_FORMAT),
      ]);
    },
    onBlur: () => {
      setFieldTouched(name);
      onBlur();
    },
    touched: get(touched, name, false),
    errors: get(errors, name, ""),
    name,
  };
};

export const bindArrayInputProps = ({
  parent,
  index,
  name,
  values,
  handleChange,
  setFieldTouched,
  touched,
  errors,
  onBlur = () => {},
}) => {
  const childName = `${parent}.${index}.${name}`;
  return {
    value: get(values, childName, ""),
    onChange: handleChange(childName),
    onBlur: () => {
      setFieldTouched(childName);
      onBlur();
    },
    touched: get(touched, childName, false),
    errors: get(errors, childName, ""),
    name: childName,
  };
};

export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const setDefaultIfEmpty = (value, defaultValue) =>
  isEmpty(value) ? defaultValue : value;

export const convertToTouched = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertToTouched(item));
  } else if (obj !== null && typeof obj === "object") {
    let newObj = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = convertToTouched(obj[key]);
      }
    }
    return newObj;
  } else {
    return true;
  }
};
